<script setup lang="ts">
import {ref, watch} from "vue";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/core/components/ui/dialog";
import {Button} from "@/core/components/ui/button";
import {CircleCheck, Sparkles} from "@lucide/vue";

const props = defineProps<{
    targetVersion: string | null;
    scheduled: boolean;
    schedule: () => Promise<void>;
}>();

const open = defineModel<boolean>("open", {required: true});

const loading = ref(false);
const failed = ref(false);

watch(open, (isOpen) => {
    if (isOpen) {
        failed.value = false;
    }
});

/**
 * Schedule the update, then leave the acknowledgment on screen.
 */
const onSchedule = async () => {
    loading.value = true;
    failed.value = false;

    try {
        await props.schedule();
    } catch {
        failed.value = true;
    } finally {
        loading.value = false;
    }
};
</script>

<template>
  <Dialog v-model:open="open">
    <DialogContent class="max-w-md">
      <template v-if="!scheduled">
        <DialogHeader>
          <div class="flex items-center justify-center size-12 rounded-2xl bg-lightPurple-lightest mb-2">
            <Sparkles class="size-6 text-primary" />
          </div>
          <DialogTitle class="text-primary font-semibold text-lg">
            Découvrez les nouvelles fonctionnalités de Connect {{ targetVersion }}
          </DialogTitle>
          <DialogDescription class="flex flex-col gap-2 text-gray-dark text-sm">
            <span class="block">
              La mise à jour sera téléchargée sur tous les postes de votre pharmacie dès qu'elle sera prête.
              Cela peut prendre 1 à 2 heures.
            </span>
            <span class="block">
              Vous pouvez continuer à travailler normalement : sur chaque poste, Connect vous proposera ensuite d'installer la mise à jour.
            </span>
          </DialogDescription>
        </DialogHeader>
        <p
          v-if="failed"
          class="text-sm text-red-light"
        >
          La mise à jour n'a pas pu être programmée. Vérifiez votre connexion et réessayez.
        </p>
        <DialogFooter>
          <Button
            class="bg-primary rounded-full w-full"
            :disabled="loading"
            @click="onSchedule"
          >
            Programmer la mise à jour
          </Button>
        </DialogFooter>
      </template>

      <template v-else>
        <DialogHeader class="items-center text-center">
          <CircleCheck class="size-12 text-secondary-darkest mb-2" />
          <DialogTitle class="text-primary font-semibold text-lg">
            Vos postes seront mis à jour sous 2 heures.
          </DialogTitle>
          <DialogDescription class="text-gray-dark text-sm">
            Sur chaque poste, Connect vous proposera d'installer la mise à jour une fois téléchargée.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            class="bg-primary rounded-full w-full"
            @click="open = false"
          >
            Fermer
          </Button>
        </DialogFooter>
      </template>
    </DialogContent>
  </Dialog>
</template>
